import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from "vue"
import {
  ClientPaymentStructureInput,
  MissionLifecycleFragmentFragment,
  MissionScopeActivity,
  MoneyFragmentFragment,
  MissionStaffingPositionDetailFragmentFragment,
  ConsultantPaymentStructureInput,
  MissionContractPayment,
  ConsultantContractPayment,
} from "@/gql"
import Checkbox from "@/components/widgets/input/CheckboxRounded.vue"
import { MoneyInputValue } from "@/components/widgets/input/MoneyInput.vue"
import StaffingPositionFeeInput from "@/modules/Missions/components/Payment/StaffingPositionFeeInput.vue"
import CreatePaymentStructureForm from "@/modules/Missions/components/Payment/CreatePaymentStructureForm.vue"
import PreviewPayments from "@/modules/Missions/components/Payment/PreviewPayments.vue"
import { PaymentFormValues } from "@/modules/Missions/components/Payment/CreatePaymentStructureForm.vue"
import Header from "@/modules/Missions/components/Payment/SetupPaymentHeader.vue"
import AttachClientPayment from "@/modules/Missions/components/Payment/AttachClientPayment.vue"
import { SetupPaymentType } from "@/modules/Missions/components/Payment/constant"
import AddNewPayment from "@/modules/Missions/components/Payment/AddNewPayment.vue"

type DeliverablesMap = {
  [key: string]: {
    [key: string]: boolean
  }
}

enum STEPS {
  ENTER_AMOUNT = "enter-amount",
  ALLOCATE_PERCENTAGES = "allocate-percentages",
  PREVIEW = "preview",
  ATTACH_DELIVERABLES = "attach-deliverables",
  PREVIEW_ATTACHED_PAYMENT = "preview-attached-payment",
  LINK_PAYMENT = "link-payment",
  ADD_NEW_PAYMENT = "add-new-payment",
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SetupPaymentForm',
  props: {
    mission: null,
    staffingPosition: null,
    isSubmitting: { type: Boolean },
    setupPaymentType: null,
    activePayment: null
  },
  emits: [
  "close",
  "complete",
  "update-consultant-fees",
  "add-new-payment-complete",
],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  mission: MissionLifecycleFragmentFragment
  staffingPosition?: MissionStaffingPositionDetailFragmentFragment | null
  isSubmitting?: boolean
  setupPaymentType?: (typeof SetupPaymentType)[keyof typeof SetupPaymentType]
  activePayment?: ConsultantContractPayment | null
}





const isConsultantPaymentSetup = computed(() => {
  return props.setupPaymentType === SetupPaymentType.Consultant
})

const isClientPaymentSetup = computed(() => {
  return props.setupPaymentType === SetupPaymentType.Client
})

const isAddNewPaymentSetup = computed(() => {
  return props.setupPaymentType === SetupPaymentType.SinglePayment
})

const step = ref(STEPS.ENTER_AMOUNT)

const form = ref<PaymentFormValues[]>([])

const consultantFees = ref<MoneyInputValue>({
  amount: props.staffingPosition?.totalFee?.amount || 0,
  currency: props.staffingPosition?.totalFee?.currency || "USD",
})

const updateConsultantFees = (fee: MoneyInputValue) => {
  consultantFees.value = fee

  if (
    consultantFees.value.amount !== props.staffingPosition?.totalFee?.amount ||
    consultantFees.value.currency !== props.staffingPosition?.totalFee?.currency
  ) {
    emit("update-consultant-fees", consultantFees.value, () => {
      step.value = STEPS.ALLOCATE_PERCENTAGES
    })
  } else {
    step.value = STEPS.ALLOCATE_PERCENTAGES
  }
}

const totalPrice = computed((): MoneyInputValue | MoneyFragmentFragment => {
  const total =
    props.setupPaymentType === SetupPaymentType.Consultant
      ? consultantFees.value
      : props.mission.missionPricingProposal?.totalPrice

  return total || { amount: "0", currency: "USD" }
})

const updatePaymentAllocations = (payments: PaymentFormValues[]) => {
  form.value = payments
  updateSelectedDeliverablesByPaymentIndex()
  step.value = STEPS.PREVIEW
}

const getAmountFromPercentage = (percentage: number | null) => {
  if (!percentage) return 0
  return (parseFloat(totalPrice.value?.amount as string) * percentage) / 100
}

const clientPaymentsInput = computed(() => {
  return form.value
    .filter((item) => item.percentage !== null)
    .map((item, index) => ({
      paymentType: item.paymentType,
      percentage: (item?.percentage as number) * 0.01,
      deliverables: attachedDeliverables.value[index].map((d) => d.prn),
    })) as ClientPaymentStructureInput[]
})

const consultantPaymentsInput = computed(() => {
  return form.value
    .filter((item) => item.percentage !== null)
    .map((item, index) => ({
      paymentType: item.paymentType,
      percentage: item?.percentage as number,
      staffingPositionDeliverables: attachedDeliverables.value[index].map((d) => ({
        missionScopeActivityId: d.prn,
      })),
      missionContractPaymentId: item.clientPayment,
    })) as unknown as ConsultantPaymentStructureInput[]
})

const onPreviewComplete = () => {
  if (isConsultantPaymentSetup.value) {
    step.value = STEPS.PREVIEW_ATTACHED_PAYMENT
  } else {
    const filteredClientPaymentsInput = clientPaymentsInput.value.filter(
      (item) => item.percentage && Number(item.percentage) > 0
    )
    emit("complete", filteredClientPaymentsInput)
  }
}

const onPreviewAttachedPaymentComplete = () => {
  const filteredConsultantPaymentsInput = consultantPaymentsInput.value.filter(
    (item) => item.percentage && Number(item.percentage) > 0
  )

  emit("complete", filteredConsultantPaymentsInput)
}

const attachClientPaymentForm = ref<{
  [key in string]: string | null
}>({})

const activePaymentIndex = ref<number>(0)

const gotoDeliverables = (index: number) => {
  activePaymentIndex.value = index
  step.value = STEPS.ATTACH_DELIVERABLES
}

const selectedDeliverablesByPaymentIndex = ref<DeliverablesMap>({})

const updateSelectedDeliverablesByPaymentIndex = () => {
  if (Object.keys(selectedDeliverablesByPaymentIndex.value).length) return

  const deliverablesMap: DeliverablesMap = { ...selectedDeliverablesByPaymentIndex.value }
  form.value.forEach((_, index) => {
    deliverablesMap[index] = {}
    attachClientPaymentForm.value[index] = null
  })
  selectedDeliverablesByPaymentIndex.value = deliverablesMap
}

const allDeliverables = computed(() => {
  return props.mission.missionScope?.activities as MissionScopeActivity[]
})

const liveUnusedDeliverables = computed(() => {
  const usedDeliverables: { [key: string]: string[] } = {}
  Object.entries(selectedDeliverablesByPaymentIndex.value).forEach(
    ([paymentIndex, deliverables]) => {
      const trueKeys = Object.keys(deliverables).filter((key) => deliverables[key])

      usedDeliverables[paymentIndex] = trueKeys
    }
  )

  return allDeliverables.value.filter((deliverable) => {
    return !Object.values(usedDeliverables).flat().includes(deliverable.id)
  })
})

const attachedDeliverables = computed(() => {
  return Object.keys(selectedDeliverablesByPaymentIndex.value).reduce(
    (deliverablesByPaymentType, paymentIndex) => {
      deliverablesByPaymentType[Number(paymentIndex)] = Object.entries(
        selectedDeliverablesByPaymentIndex.value[Number(paymentIndex)]
      )
        .filter(([, value]) => {
          return value
        })
        .map(([key]) => allDeliverables.value.find((d) => d.id === key)) as MissionScopeActivity[]

      return deliverablesByPaymentType
    },
    {} as Record<number, MissionScopeActivity[]>
  )
})

const renderedDeliverables = computed(() => {
  const payload = new Set(
    attachedDeliverables.value?.[activePaymentIndex.value].concat(liveUnusedDeliverables.value)
  )

  return [...payload].sort((a, b) => a?.id - b?.id) as MissionScopeActivity[]
})

const resetForm = () => {
  form.value = []
  selectedDeliverablesByPaymentIndex.value = {}
  step.value = STEPS.ENTER_AMOUNT
  activePaymentIndex.value = 0
}

const onClose = () => {
  resetForm()
  emit("close")
}

const gotoLinkPayment = (index: number) => {
  activePaymentIndex.value = index
  step.value = STEPS.LINK_PAYMENT
}

const onDoneAttachingDeliverables = () => {
  if (isAddNewPaymentSetup.value) return (step.value = STEPS.ADD_NEW_PAYMENT)
  else step.value = STEPS.PREVIEW
}

const updateClientPayment = (index: number, prn: string) => {
  const payment = form.value[index]

  if (payment) {
    payment.clientPayment = prn
  }
}

const goToInitialStep = () => {
  if (isClientPaymentSetup.value) {
    emit("close")
  } else {
    step.value = STEPS.ENTER_AMOUNT
  }
}

const navigateFromDeliverables = () => {
  if (isAddNewPaymentSetup.value) {
    step.value = STEPS.ADD_NEW_PAYMENT
  } else {
    step.value = STEPS.PREVIEW
  }
}

const missionContractPayments = computed(() => {
  return props.mission.missionContract?.missionContractPayments as MissionContractPayment[]
})
const updateNewPaymentForm = (payment: PaymentFormValues) => {
  form.value = [
    {
      name: payment.name,
      amount: {
        amount: payment.amount?.amount ?? null,
        currency: payment.amount?.currency ?? "USD",
      },
      deliverables: payment.deliverables,
      clientPayment: payment.clientPayment,
      description: payment.description,
    },
  ]
}

const onGoBackFromLinkPayment = () => {
  if (isAddNewPaymentSetup.value) step.value = STEPS.ADD_NEW_PAYMENT
  else step.value = STEPS.PREVIEW_ATTACHED_PAYMENT
}

const onLinkClientPaymentComplete = () => {
  if (isAddNewPaymentSetup.value) step.value = STEPS.ADD_NEW_PAYMENT
  else step.value = STEPS.PREVIEW_ATTACHED_PAYMENT
}

const onGotoDeliverablesFromAddNewPayment = (index: number) => {
  updateSelectedDeliverablesByPaymentIndex()
  gotoDeliverables(index)
}

const onAddNewPaymentComplete = async () => {
  const payment = form.value[0]

  emit("add-new-payment-complete", {
    ...payment,
    missionContractPaymentId: payment.clientPayment,
    deliverables: attachedDeliverables.value[0]?.map((d) => ({
      missionScopeActivityId: d.prn,
    })),
  })
}

const instantiateAddNewPaymentForm = () => {
  const deliverables = props.activePayment?.staffingPositionDeliverables?.map(
    (d) => d.missionScopeActivity
  )

  selectedDeliverablesByPaymentIndex.value = {
    0:
      deliverables?.reduce((acc, d) => {
        if (d) acc[d.id] = true
        return acc
      }, {} as { [key: string]: boolean }) || {},
  }

  updateNewPaymentForm({
    name: props.activePayment?.name ?? "",
    amount: {
      amount: props.activePayment?.amount?.amount ?? null,
      currency: props.activePayment?.amount?.currency ?? "USD",
    },
    deliverables: deliverables ?? [],
    clientPayment: props.activePayment?.missionContractPayment?.id ?? null,
    description: props.activePayment?.description ?? "",
  })
}

onMounted(() => {
  if (isClientPaymentSetup.value) {
    step.value = STEPS.ALLOCATE_PERCENTAGES
  }
  if (isAddNewPaymentSetup.value) {
    step.value = STEPS.ADD_NEW_PAYMENT
    instantiateAddNewPaymentForm()
  }
})

expose({
  resetForm,
})

return { __sfc: true,props, emit, isConsultantPaymentSetup, isClientPaymentSetup, isAddNewPaymentSetup, STEPS, step, form, consultantFees, updateConsultantFees, totalPrice, updatePaymentAllocations, getAmountFromPercentage, clientPaymentsInput, consultantPaymentsInput, onPreviewComplete, onPreviewAttachedPaymentComplete, attachClientPaymentForm, activePaymentIndex, gotoDeliverables, selectedDeliverablesByPaymentIndex, updateSelectedDeliverablesByPaymentIndex, allDeliverables, liveUnusedDeliverables, attachedDeliverables, renderedDeliverables, resetForm, onClose, gotoLinkPayment, onDoneAttachingDeliverables, updateClientPayment, goToInitialStep, navigateFromDeliverables, missionContractPayments, updateNewPaymentForm, onGoBackFromLinkPayment, onLinkClientPaymentComplete, onGotoDeliverablesFromAddNewPayment, onAddNewPaymentComplete, instantiateAddNewPaymentForm, Checkbox, StaffingPositionFeeInput, CreatePaymentStructureForm, PreviewPayments, Header, AttachClientPayment, AddNewPayment }
}

})